// Mensagem e link de envio do orçamento (WhatsApp / link copiado) em um só lugar (ORCA-1921).
import { environment } from 'environments/environment';

export interface ProposalShareInfo {
    proposalNumber?: number | string | null;
    shortDescription?: string | null;
    dateProposal?: Date | string | null;
    expiresIn?: number | string | null;
    client?: { name?: string | null; cpfCnpj?: string | null } | null;
}

/** Data de validade (data do orçamento + dias de validade) em dd/mm/aaaa. */
export function proposalValidUntil(proposal: ProposalShareInfo): string {
    // Parse de "aaaa-mm-dd" como data local para evitar o fuso (UTC) mudar o dia.
    let baseDate: Date;
    const dp: any = proposal.dateProposal;
    if (dp instanceof Date) {
        baseDate = new Date(dp.getTime());
    } else if (typeof dp === 'string') {
        const m = dp.match(/^(\d{4})-(\d{2})-(\d{2})/);
        baseDate = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dp);
    } else {
        baseDate = new Date();
    }

    baseDate.setDate(baseDate.getDate() + (Number(proposal.expiresIn) || 0));
    const dia = baseDate.getDate().toString().padStart(2, '0');
    const mes = (baseDate.getMonth() + 1).toString().padStart(2, '0');
    return `${dia}/${mes}/${baseDate.getFullYear()}`;
}

/** Como chamar o cliente na saudação: primeiro nome de pessoa; nome completo de empresa (CNPJ). */
export function clientGreetingName(client: ProposalShareInfo['client']): string {
    const name = (client?.name || '').trim();
    if (!name) {
        return '';
    }
    const isCompany = (client?.cpfCnpj || '').replace(/\D/g, '').length === 14;
    return isCompany ? name : name.split(/\s+/)[0];
}

/**
 * "Olá, Maria! Segue o orçamento de Pinturas Silva:
 *
 *  #2 - Pintura do apartamento
 *  Orçamento válido até: 30/10/2026
 *  Acesse o orçamento: https://..."
 */
export function buildProposalShareMessage(proposal: ProposalShareInfo, link: string, companyName?: string | null): string {
    const greetingName = clientGreetingName(proposal.client);
    const greeting = greetingName ? `Olá, ${greetingName}!` : 'Olá!';
    const company = (companyName || '').trim();
    const intro = company ? `${greeting} Segue o orçamento de ${company}:` : `${greeting} Segue o orçamento:`;

    const title = [proposal.proposalNumber ? `#${proposal.proposalNumber}` : '', proposal.shortDescription || '']
        .filter(Boolean)
        .join(' - ');

    return [
        intro,
        '',
        title,
        `Orçamento válido até: ${proposalValidUntil(proposal)}`,
        `Acesse o orçamento: ${link}`,
    ].filter((line, index) => line !== '' || index === 1).join('\n');
}

/** Telefone para o wa.me/api.whatsapp: só dígitos, com o 55 uma única vez. */
export function whatsappPhone(phone: string | null | undefined): string {
    let digits = (phone || '').replace(/\D/g, '').replace(/^0+/, '');
    if (!(digits.length >= 12 && digits.startsWith('55'))) {
        digits = `55${digits}`;
    }
    return digits;
}

export function buildWhatsappUrl(phone: string | null | undefined, text: string): string {
    return `https://api.whatsapp.com/send?phone=${whatsappPhone(phone)}&text=${encodeURIComponent(text)}`;
}

/**
 * ORCA-1945: link de "Visualizar como cliente". É o mesmo orçamento do link enviado ao cliente,
 * em outro endereço, que não avisa a empresa da visualização nem permite aprovar/reprovar.
 */
export function clientPreviewUrl(tokenLink: string | null | undefined): string | null {
    return tokenLink ? `${environment.baseUrlHotsite}/email/proposals/${tokenLink}/visualizar` : null;
}
